export type MilestoneStatus = 'meets' | 'at-risk' | 'needs-info' | 'not-applicable';

export interface AcademicMilestone {
  id: string;
  category: 'recognition' | 'standing' | 'financial-aid' | 'scholarship';
  title: string;
  status: MilestoneStatus;
  summary: string;
  detail: string;
  sourceLabel: string;
  sourceUrl: string;
}

export interface AcademicMilestoneInput {
  school?: string;
  academicYear?: string;
  cumulativeGpa?: number | null;
  termGpa?: number | null;
  termLetterCredits?: number | null;
  hasTermF?: boolean;
  consecutiveTermsBelowTwo?: number | null;
  cumulativeCompletedCredits?: number | null;
  cumulativeAttemptedCredits?: number | null;
  annualDegreeCredits?: number | null;
  currentTermDegreeCredits?: number | null;
  scholarshipAward?: string | null;
  scholarshipGpa?: number | null;
}

export const ACADEMIC_POLICY_REVIEWED_AT = '2026-08-28';

export const SCHOLARSHIP_OPTIONS = [
  "Chancellor's Promise",
  "Chancellor's Scholarship",
  "Dean's Promise",
  "Dean's Three-year Scholarship",
  'Distinguished Scholar Award',
  'Francis B. and Paige D. L’Hommedieu Scholarship',
  'Global Knights Award',
  'Henry Rutgers Scholarship and Trustee Awards',
  'National Scholarship',
  'NJ STARS II',
  'Rutgers James Dickson Carr Scholarship',
  'Rutgers James Dickson Carr Scholarship and Honors College Alumni Scholarship',
  'Rutgers Presidential Scholarship',
  'Rutgers ROTC Award',
  'Scarlet Scholarship',
  "The Dean's Scholarship",
  'Trustee Awards',
] as const;

const SOURCES = {
  sasDean: 'https://sasundergrad.rutgers.edu/resources/deans-list',
  sasHonors: 'https://sasundergrad.rutgers.edu/resources/policies/rutgers-university-guideline/graduation-honors',
  sasStanding: 'https://sasundergrad.rutgers.edu/resources/academic-review',
  soe: 'https://soe.rutgers.edu/academic-advising-and-policies/academic-policies/deans-list-and-honors',
  sap: 'https://scarlethub.rutgers.edu/financial-services/eligibility/satisfactory-academic-progress-sap/',
  scholarship: 'https://scarlethub.rutgers.edu/financial-services/types-of-aid/scholarships/merit-based-scholarship-eligibility-criteria/',
} as const;

function finite(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isSas(school?: string) {
  return /arts and sciences/i.test(school ?? '');
}

function isEngineering(school?: string) {
  return /engineering/i.test(school ?? '');
}

function normalizeYear(year?: string) {
  const value = (year ?? '').toLowerCase();
  if (/first|fresh/.test(value)) return 'first-year';
  if (/soph|second/.test(value)) return 'sophomore';
  if (/jun|third/.test(value)) return 'junior';
  if (/sen|fourth/.test(value)) return 'senior';
  return null;
}

function gpaBand(gpa: number, bands: Array<{ min: number; label: string }>) {
  return bands.find((band) => gpa >= band.min)?.label ?? null;
}

function evaluateDeansList(input: AcademicMilestoneInput): AcademicMilestone {
  if (!isSas(input.school) && !isEngineering(input.school)) {
    return {
      id: 'deans-list', category: 'recognition', title: "Dean's List", status: 'needs-info',
      summary: 'Select your Rutgers school to load the applicable rule.',
      detail: "Dean's List criteria differ by school and cannot be inferred from GPA alone.",
      sourceLabel: 'Rutgers school policies', sourceUrl: SOURCES.sasDean,
    };
  }

  if (!finite(input.termGpa) || !finite(input.termLetterCredits)) {
    return {
      id: 'deans-list', category: 'recognition', title: "Dean's List", status: 'needs-info',
      summary: 'Term GPA and letter-graded credits are needed.',
      detail: isEngineering(input.school)
        ? 'Engineering also requires the applicable class-year threshold, no F, and qualifying engineering or engineering-related credits.'
        : 'SAS requires a 3.500 term GPA over at least 12 letter-graded credits.',
      sourceLabel: isEngineering(input.school) ? 'Rutgers Engineering' : 'Rutgers SAS',
      sourceUrl: isEngineering(input.school) ? SOURCES.soe : SOURCES.sasDean,
    };
  }

  if (isSas(input.school)) {
    const meets = input.termGpa >= 3.5 && input.termLetterCredits >= 12;
    return {
      id: 'deans-list', category: 'recognition', title: "Dean's List", status: meets ? 'meets' : 'at-risk',
      summary: meets ? 'Meets the published SAS GPA and credit criteria.' : 'Does not currently meet the published SAS GPA and credit criteria.',
      detail: `SAS requires 3.500 over at least 12 letter-graded credits. Current scenario: ${input.termGpa.toFixed(3)} over ${input.termLetterCredits} credits.`,
      sourceLabel: 'Rutgers SAS', sourceUrl: SOURCES.sasDean,
    };
  }

  const year = normalizeYear(input.academicYear);
  const thresholds: Record<string, number> = { 'first-year': 3.2, sophomore: 3.3, junior: 3.4, senior: 3.5 };
  if (!year) {
    return {
      id: 'deans-list', category: 'recognition', title: "Dean's List", status: 'needs-info',
      summary: 'Academic year is needed for the Engineering threshold.',
      detail: 'Engineering thresholds rise from 3.2 for first-year students to 3.5 for seniors.',
      sourceLabel: 'Rutgers Engineering', sourceUrl: SOURCES.soe,
    };
  }
  const threshold = thresholds[year];
  const meets = input.termGpa >= threshold && input.termLetterCredits >= 12 && !input.hasTermF;
  return {
    id: 'deans-list', category: 'recognition', title: "Dean's List", status: meets ? 'meets' : 'at-risk',
    summary: meets ? 'Meets the published Engineering GPA, credit, and no-F criteria.' : 'Does not currently meet every published Engineering criterion.',
    detail: `${year.replace('-', ' ')} threshold: ${threshold.toFixed(1)} over at least 12 qualifying letter-graded credits with no F.`,
    sourceLabel: 'Rutgers Engineering', sourceUrl: SOURCES.soe,
  };
}

function evaluateHonors(input: AcademicMilestoneInput): AcademicMilestone {
  if (!finite(input.cumulativeGpa)) {
    return {
      id: 'graduation-honors', category: 'recognition', title: 'Graduation honors', status: 'needs-info',
      summary: 'Cumulative GPA is needed.', detail: 'Honors thresholds differ by Rutgers school.',
      sourceLabel: 'Rutgers school policies', sourceUrl: SOURCES.sasHonors,
    };
  }
  if (isSas(input.school)) {
    const band = gpaBand(input.cumulativeGpa, [
      { min: 3.85, label: 'summa cum laude' }, { min: 3.7, label: 'magna cum laude' }, { min: 3.5, label: 'cum laude' },
    ]);
    return {
      id: 'graduation-honors', category: 'recognition', title: 'Graduation honors', status: band ? 'meets' : 'at-risk',
      summary: band ? `Current GPA is in the SAS ${band} band.` : 'Current GPA is below the first SAS honors threshold.',
      detail: 'SAS evaluates the final cumulative GPA and does not round averages up to the next honors threshold.',
      sourceLabel: 'Rutgers SAS', sourceUrl: SOURCES.sasHonors,
    };
  }
  if (isEngineering(input.school)) {
    const band = gpaBand(input.cumulativeGpa, [
      { min: 3.65, label: 'highest honors' }, { min: 3.4, label: 'high honors' }, { min: 3.2, label: 'honors' },
    ]);
    return {
      id: 'graduation-honors', category: 'recognition', title: 'Graduation honors', status: band ? 'meets' : 'at-risk',
      summary: band ? `Current GPA is in the Engineering ${band} band.` : 'Current GPA is below the first Engineering honors threshold.',
      detail: 'This is a projection from the current cumulative GPA; Rutgers applies the rule to the final record.',
      sourceLabel: 'Rutgers Engineering', sourceUrl: SOURCES.soe,
    };
  }
  return {
    id: 'graduation-honors', category: 'recognition', title: 'Graduation honors', status: 'needs-info',
    summary: 'Select a supported Rutgers school to load its honors thresholds.',
    detail: 'A GPA alone is not enough to apply a school-specific honors rule.',
    sourceLabel: 'Rutgers school policies', sourceUrl: SOURCES.sasHonors,
  };
}

function evaluateStanding(input: AcademicMilestoneInput): AcademicMilestone {
  if (!isSas(input.school)) {
    return {
      id: 'academic-standing', category: 'standing', title: 'Academic standing', status: 'needs-info',
      summary: 'School-specific standing rules are not yet loaded for this school.',
      detail: 'Scarlet Sync does not apply the SAS warning/probation sequence outside SAS.',
      sourceLabel: 'Rutgers SAS', sourceUrl: SOURCES.sasStanding,
    };
  }
  if (!finite(input.termGpa)) {
    return {
      id: 'academic-standing', category: 'standing', title: 'Academic standing', status: 'needs-info',
      summary: 'Term GPA is needed.', detail: 'SAS reviews standing after the Fall and Spring terms.',
      sourceLabel: 'Rutgers SAS', sourceUrl: SOURCES.sasStanding,
    };
  }
  if (input.termGpa >= 2) {
    return {
      id: 'academic-standing', category: 'standing', title: 'Academic standing', status: 'meets',
      summary: 'Scenario meets the published 2.000 SAS term threshold.',
      detail: 'This planning result is not an official standing determination.',
      sourceLabel: 'Rutgers SAS', sourceUrl: SOURCES.sasStanding,
    };
  }
  const repeated = (input.consecutiveTermsBelowTwo ?? 0) >= 2;
  return {
    id: 'academic-standing', category: 'standing', title: 'Academic standing', status: 'at-risk',
    summary: repeated ? 'Scenario may align with the SAS probation rule.' : 'Scenario may align with the SAS warning rule.',
    detail: repeated ? 'SAS describes probation after two or more consecutive Fall/Spring term GPAs below 2.000.' : 'SAS describes warning after a term below 2.000 following satisfactory standing.',
    sourceLabel: 'Rutgers SAS', sourceUrl: SOURCES.sasStanding,
  };
}

function sapThreshold(completedCredits: number) {
  if (completedCredits <= 30) return { completionRate: 50, gpa: 1.4 };
  if (completedCredits <= 60) return { completionRate: 60, gpa: 1.7 };
  if (completedCredits <= 90) return { completionRate: 70, gpa: 2 };
  return { completionRate: 75, gpa: 2 };
}

export function evaluateSap(input: AcademicMilestoneInput): AcademicMilestone {
  if (!finite(input.cumulativeGpa) || !finite(input.cumulativeCompletedCredits) || !finite(input.cumulativeAttemptedCredits) || input.cumulativeAttemptedCredits <= 0) {
    return {
      id: 'sap', category: 'financial-aid', title: 'Financial-aid SAP', status: 'needs-info',
      summary: 'Cumulative GPA plus completed and attempted credits are needed.',
      detail: 'Rutgers evaluates both qualitative GPA and quantitative completion-rate standards.',
      sourceLabel: 'Rutgers Scarlet Hub', sourceUrl: SOURCES.sap,
    };
  }
  const threshold = sapThreshold(input.cumulativeCompletedCredits);
  const rate = input.cumulativeCompletedCredits / input.cumulativeAttemptedCredits * 100;
  const meets = input.cumulativeGpa >= threshold.gpa && rate >= threshold.completionRate;
  return {
    id: 'sap', category: 'financial-aid', title: 'Financial-aid SAP', status: meets ? 'meets' : 'at-risk',
    summary: meets ? 'Meets the published GPA and completion-rate thresholds.' : 'Below at least one published SAP threshold.',
    detail: `Current inputs: ${input.cumulativeGpa.toFixed(3)} GPA and ${rate.toFixed(1)}% completion. Required for this completed-credit band: ${threshold.gpa.toFixed(2)} and ${threshold.completionRate}%. The 150% maximum-timeframe rule is separate.`,
    sourceLabel: 'Rutgers Scarlet Hub', sourceUrl: SOURCES.sap,
  };
}

type ScholarshipRule = { gpa?: number; annualCredits?: number; termCredits?: number; extra?: string; sapOnly?: boolean };

const scholarshipRules: Record<string, ScholarshipRule> = {
  "Chancellor's Promise": { gpa: 2.5, annualCredits: 24, termCredits: 12 },
  "Chancellor's Scholarship": { gpa: 3.25, annualCredits: 24, termCredits: 12 },
  "Dean's Promise": { gpa: 2.5, annualCredits: 24, termCredits: 12 },
  "Dean's Three-year Scholarship": { gpa: 3.25, annualCredits: 24, termCredits: 12 },
  'Distinguished Scholar Award': { gpa: 3.25, annualCredits: 24, termCredits: 12 },
  'Francis B. and Paige D. L’Hommedieu Scholarship': { sapOnly: true },
  'Global Knights Award': { gpa: 3.25, annualCredits: 24, termCredits: 12 },
  'Henry Rutgers Scholarship and Trustee Awards': { gpa: 3.25, annualCredits: 24, termCredits: 12 },
  'National Scholarship': { gpa: 3.25, annualCredits: 24, termCredits: 12 },
  'NJ STARS II': { gpa: 3.25, termCredits: 12, extra: 'The published rule evaluates GPA at the beginning of the third semester.' },
  'Rutgers James Dickson Carr Scholarship': { gpa: 3.25, annualCredits: 24, termCredits: 12 },
  'Rutgers James Dickson Carr Scholarship and Honors College Alumni Scholarship': { gpa: 3.25, annualCredits: 24, termCredits: 12, extra: 'Honors College standing, service, courses, and participation must also be confirmed.' },
  'Rutgers Presidential Scholarship': { gpa: 3.5, annualCredits: 24, termCredits: 12, extra: 'Honors College participation and residence conditions must also be confirmed.' },
  'Rutgers ROTC Award': { gpa: 2, annualCredits: 24, termCredits: 12, extra: 'ROTC participation and branch standing must also be confirmed.' },
  'Scarlet Scholarship': { gpa: 3.25, annualCredits: 24, termCredits: 12 },
  "The Dean's Scholarship": { gpa: 3.25, annualCredits: 24, termCredits: 12 },
  'Trustee Awards': { gpa: 3.25, annualCredits: 24, termCredits: 12 },
};

function evaluateScholarship(input: AcademicMilestoneInput, sap: AcademicMilestone): AcademicMilestone {
  const award = input.scholarshipAward?.trim();
  if (!award) {
    return {
      id: 'scholarship', category: 'scholarship', title: 'Scholarship renewal', status: 'needs-info',
      summary: 'Select an existing Rutgers award to check its published academic criteria.',
      detail: 'Scarlet Sync will not infer a scholarship from GPA alone. The award letter remains authoritative.',
      sourceLabel: 'Rutgers Scarlet Hub', sourceUrl: SOURCES.scholarship,
    };
  }
  const rule = scholarshipRules[award];
  if (!rule) {
    return {
      id: 'scholarship', category: 'scholarship', title: award, status: 'needs-info',
      summary: 'No maintained New Brunswick renewal rule is available for this award.',
      detail: 'Use the award letter and the official scholarship office source.',
      sourceLabel: 'Rutgers Scarlet Hub', sourceUrl: SOURCES.scholarship,
    };
  }
  if (rule.sapOnly) {
    return {
      id: 'scholarship', category: 'scholarship', title: award, status: sap.status,
      summary: sap.status === 'meets' ? 'Meets the currently modeled SAP requirement.' : 'SAP information is incomplete or below a published threshold.',
      detail: 'The official award page specifies maintaining Satisfactory Academic Progress.',
      sourceLabel: 'Rutgers Scarlet Hub', sourceUrl: SOURCES.scholarship,
    };
  }
  if (!finite(input.scholarshipGpa) || (rule.annualCredits && !finite(input.annualDegreeCredits)) || (rule.termCredits && !finite(input.currentTermDegreeCredits))) {
    return {
      id: 'scholarship', category: 'scholarship', title: award, status: 'needs-info',
      summary: 'GPA and applicable enrollment/completion credits are needed.',
      detail: 'Summer credits do not count toward the published annual renewal credit requirement.',
      sourceLabel: 'Rutgers Scarlet Hub', sourceUrl: SOURCES.scholarship,
    };
  }
  const meetsAcademic = input.scholarshipGpa >= (rule.gpa ?? 0)
    && (!rule.termCredits || input.currentTermDegreeCredits! >= rule.termCredits)
    && (!rule.annualCredits || input.annualDegreeCredits! >= rule.annualCredits);
  const needsExtra = meetsAcademic && Boolean(rule.extra);
  return {
    id: 'scholarship', category: 'scholarship', title: award,
    status: needsExtra ? 'needs-info' : meetsAcademic ? 'meets' : 'at-risk',
    summary: needsExtra
      ? 'Meets the modeled GPA and credit criteria; additional conditions still need confirmation.'
      : meetsAcademic
        ? 'Meets the published GPA and credit criteria modeled here.'
        : 'Below at least one published GPA or credit criterion.',
    detail: `Modeled requirement: ${rule.gpa?.toFixed(2)} GPA${rule.termCredits ? `, ${rule.termCredits} credits each semester` : ''}${rule.annualCredits ? `, and ${rule.annualCredits} completed during the academic year` : ''}.${rule.extra ? ` ${rule.extra}` : ''}`,
    sourceLabel: 'Rutgers Scarlet Hub', sourceUrl: SOURCES.scholarship,
  };
}

export function evaluateAcademicMilestones(input: AcademicMilestoneInput): AcademicMilestone[] {
  const sap = evaluateSap(input);
  return [evaluateDeansList(input), evaluateHonors(input), evaluateStanding(input), sap, evaluateScholarship(input, sap)];
}
